import { useState, useRef, useEffect, useMemo, forwardRef } from "react";
import { useHistory } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface SearchableItem {
  label: string;
  href: string;
  groupLabel?: string;
  parentLabel?: string;
  external?: boolean;
}

interface SidebarSearchProps {
  items: SearchableItem[];
  collapsed?: boolean;
}

export const SidebarSearch = forwardRef<HTMLInputElement, SidebarSearchProps>(
  function SidebarSearch({ items, collapsed }, forwardedRef) {
  const history = useHistory();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = (forwardedRef as React.RefObject<HTMLInputElement>) || internalRef;
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    setQuery("");
    setIsOpen(false);
    setSelectedIndex(0);
  };

  const navigateToItem = (item: SearchableItem) => {
    if (item.external) {
      window.location.assign(item.href);
    } else {
      history.push(item.href);
    }
    handleClose();
  };

  // Filter items based on query
  const filteredItems = useMemo(() => {
    if (!query.trim()) return [];
    const lowerQuery = query.toLowerCase();
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(lowerQuery) ||
        (item.groupLabel?.toLowerCase().includes(lowerQuery) ?? false) ||
        (item.parentLabel?.toLowerCase().includes(lowerQuery) ?? false)
    ).slice(0, 8); // Limit to 8 results
  }, [items, query]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredItems]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && filteredItems.length > 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      selectedElement?.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, filteredItems.length]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen || !query) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen, query]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!filteredItems.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredItems.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
        break;
      case "Enter":
        e.preventDefault();
        if (filteredItems[selectedIndex]) {
          navigateToItem(filteredItems[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        handleClose();
        break;
    }
  };

  const handleItemClick = (item: SearchableItem) => {
    navigateToItem(item);
  };

  // If collapsed, show a simple search icon button
  if (collapsed) {
    return (
      <button
        onClick={() => {
          // Could open a modal search in the future
        }}
        className={cn(
          "flex items-center justify-center w-full h-9 rounded-md",
          "text-sidebar-foreground/60 hover:text-sidebar-foreground",
          "hover:bg-sidebar-accent/50 transition-colors duration-150"
        )}
        title="Search menu"
      >
        <Search className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div ref={containerRef} className="relative px-2 mb-4">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-sidebar-foreground/40" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search menu..."
          className={cn(
            "w-full h-9 pl-8 pr-8 rounded-md",
            "bg-sidebar-accent/30 border border-sidebar-border",
            "text-sm text-sidebar-foreground placeholder:text-sidebar-foreground/40",
            "focus:outline-none focus:ring-2 focus:ring-sidebar-ring focus:border-transparent",
            "transition-all duration-150"
          )}
          role="combobox"
          aria-expanded={isOpen && query.length > 0 && filteredItems.length > 0}
          aria-controls="search-results"
          aria-autocomplete="list"
          aria-label="Search navigation menu"
        />
        {query && (
          <button
            onClick={handleClose}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-sidebar-accent"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5 text-sidebar-foreground/40 hover:text-sidebar-foreground" />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && query && filteredItems.length > 0 && (
        <div
          id="search-results"
          className={cn(
            "absolute z-50 left-2 right-2 top-full mt-1",
            "bg-popover border border-border rounded-md shadow-lg",
            "max-h-80 overflow-y-auto"
          )}
          role="listbox"
        >
          <ul ref={listRef} className="py-1">
            {filteredItems.map((item, index) => (
              <li key={item.href} role="option" aria-selected={index === selectedIndex}>
                <button
                  onClick={() => handleItemClick(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={cn(
                    "flex items-center w-full px-3 py-2 text-left",
                    "transition-colors duration-100",
                    index === selectedIndex
                      ? "bg-accent text-accent-foreground"
                      : "text-foreground hover:bg-accent/50"
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {item.label}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground truncate">
                      <span>{item.groupLabel}</span>
                      {item.parentLabel && (
                        <>
                          <ArrowRight className="h-3 w-3 shrink-0" />
                          <span>{item.parentLabel}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground ml-2" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* No results message */}
      {isOpen && query && filteredItems.length === 0 && (
        <div
          className={cn(
            "absolute z-50 left-2 right-2 top-full mt-1",
            "bg-popover border border-border rounded-md shadow-lg",
            "px-3 py-4 text-center"
          )}
        >
          <Search className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No results found</p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            Try a different search term
          </p>
        </div>
      )}

    </div>
  );
});
